import { Container, Grid, Typography } from "@mui/material";
import { unwrapResult } from "@reduxjs/toolkit";
import { AsolviSwitch } from "components/AsolviSwitch";
import FullScreenDialog from "components/FullScreenDialog";
import PrimaryButton from "components/PrimaryButton";
import { ConsentObject, getUserpilotConsent, isAbortError } from "helpers";
import { UserConsentType, UserConsentTypeEnum } from "operations/schema/schema";
import { FC, useCallback, useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import ReactMarkdown from "react-markdown";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "store";
import { addSnackbarMessage } from "store/slices/snackbar.store";
import { updateUserConsent } from "store/slices/user.store";

interface UserConsentProps {
  open: boolean;
  setOpen: (open: boolean) => void;
}

export const UserpilotUserConsentDialog: FC<UserConsentProps> = (props) => {
  const { open, setOpen } = props;
  const intl = useIntl();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { languageVar } = useAppSelector((state) => state.user);
  const [consentAccepted, setConsentAccepted] = useState(false);
  const [consentObject, setConsentObject] = useState<ConsentObject>();

  useEffect(() => {
    getUserpilotConsent(languageVar).then((c) => setConsentObject(c));
  }, [languageVar]);

  const addUserConsent = useCallback(
    async (accepted: boolean = false) => {
      let consent: UserConsentType = {
        accepted: accepted,
        version: consentObject!.version,
        type: UserConsentTypeEnum.Userpilot,
      };

      dispatch(updateUserConsent({ consent }))
        .then(unwrapResult)
        .then(() => setOpen(false))
        .catch((e) => {
          if (isAbortError(e)) return;
          dispatch(addSnackbarMessage({ key: "AddUserConsent-fail" }));
        });
    },
    [consentObject, dispatch, setOpen]
  );

  if (!consentObject) {
    return null;
  }
  return (
    <FullScreenDialog
      open={open}
      setOpen={setOpen}
      title={intl.formatMessage({ id: "userConsent.dialogTitle" })}
      onClose={() => {
        addUserConsent();
        if (consentObject.required) {
          navigate("/logout", { replace: true });
        }
      }}
      child={
        <Container data-testid="UserpilotUserConsentDialog" className="e2e-consent-dialog">
          <Grid container alignItems="center" justifyContent="center">
            <Typography
              component="div"
              whiteSpace="pre-line"
              sx={{ paddingBottom: "20px", paddingTop: "20px" }}
            >
              <ReactMarkdown className="markdown-text" children={consentObject.text} />
            </Typography>
          </Grid>
          <Grid container alignItems="center" justifyContent="space-between">
            <Grid item>
              <Typography variant="body2">
                <FormattedMessage id="userConsent.toggleText" />
              </Typography>
            </Grid>
            <Grid item>
              <AsolviSwitch
                key="userpilotConsent"
                className="e2e-accept-consent-toggle"
                checked={consentAccepted}
                onChange={() => {
                  setConsentAccepted(!consentAccepted);
                }}
              />
            </Grid>
          </Grid>
          <Grid container alignItems="center" justifyContent="center" sx={{ paddingTop: "20px" }}>
            <Grid item alignItems="center" xs={12}>
              <PrimaryButton
                className="e2e-save-consent-button"
                type="submit"
                variant="contained"
                disabled={!consentAccepted && consentObject.required}
                fullWidth
                onClick={() => {
                  addUserConsent(consentAccepted);
                }}
              >
                <FormattedMessage id="trackingConsent.saveAndContinue" />
              </PrimaryButton>
            </Grid>
          </Grid>
        </Container>
      }
    />
  );
};
